import { useState, useCallback, useEffect } from "react";
import api from "../api/axios";

/* ─── tipos ─── */
export interface Usuario {
  id: number;
  email: string;
  nombreCompleto: string | null;
  rol:
    | "profesor"
    | "admin"
    | "att_familias"
    | "admisiones"
    | "cliente"
    | "att_alumnado"
    | "att_profesorado"
    | "facturacion"
    | "hr"
    | "consultor";
  /** En MySQL puede venir como TINYINT; lo normalizamos a boolean en el front si hiciera falta */
  activo: boolean | 0 | 1 | "0" | "1" | "true" | "false";
  /** ISO YYYY-MM-DD — obligatorio si rol ≠ cliente */
  fechaIncorporacion?: string;
}

/* ─── opciones del hook ─── */
export interface UseUsuariosOpts {
  /** Excluye a los usuarios con rol «cliente» en el front (además de cualquier filtro backend) */
  sinClientes?: boolean;
  /** Si es true, añade ?activo=true a la petición y filtra en el front por si acaso */
  soloActivos?: boolean;
  /** Filtra por rol (además de pedirlo por query param) */
  rol?: Usuario["rol"];
  /** Lanza la carga automáticamente al montar el hook (por defecto true) */
  auto?: boolean;
}

/* ─── util: normalizar truthy/falsey para 'activo' ─── */
const isActiveTrue = (v: any) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["1", "true", "si", "sí", "activo", "active", "enabled", "yes"].includes(s)) return true;
    if (["0", "false", "no", "inactivo", "inactive", "baja", "archivado", "disabled"].includes(s))
      return false;
  }
  return !!v;
};

/* ─── CRUD / listado ─── */
export function useUsuarios(opts: UseUsuariosOpts = {}) {
  const { sinClientes = false, soloActivos = false, rol, auto = true } = opts;

  const [rows, setRows] = useState<Usuario[]>([]);
  const [loading, setL] = useState(false);
  const [error, setErr] = useState<unknown>(null);

  /* GET /usuarios  (usa los query params del backend) */
  const refresh = useCallback(() => {
    setL(true);
    setErr(null);

    const params: any = {};
    if (rol) params.rol = rol;
    if (soloActivos) params.activo = true;

    return api
      .get<any[]>("/usuarios", { params })
      .then((r) => {
        // ⬇️ Mapeo para asegurar fechaIncorporacion en camelCase y formato YYYY-MM-DD
        let data: Usuario[] = (r.data ?? []).map((u: any) => ({
          ...u,
          fechaIncorporacion:
            u.fechaIncorporacion ??
            (u.fecha_incorporacion ? String(u.fecha_incorporacion).substring(0, 10) : undefined),
        }));

        // Filtros de respaldo en front (por si el backend no filtró)
        if (rol) data = data.filter((u) => u.rol === rol);
        if (soloActivos) data = data.filter((u) => isActiveTrue(u.activo));
        if (sinClientes) data = data.filter((u) => u.rol !== "cliente");

        setRows(data);
      })
      .catch((e) => {
        setErr(e);
        setRows([]);
      })
      .finally(() => setL(false));
  }, [rol, soloActivos, sinClientes]);

  useEffect(() => {
    if (auto) refresh();
  }, [auto, refresh]);

  return { rows, loading, error, refresh };
}
